import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { CoverageChecker } from "../components/CoverageChecker";
import { CtaBand } from "../components/CtaBand";
import { CITIES } from "../data/cities";
import { CITY_IMAGES } from "../data/site";

export default function LocationsIndex() {
    return (
        <div data-testid="locations-index-page">
            <Seo
                title="Security Company Locations Scotland | 46+ Towns & Cities | Xtreme Protection"
                description="Xtreme Protection covers 46+ Scottish towns and cities: Glasgow, Edinburgh, Aberdeen, Dundee, Inverness, Stirling, Perth and more. Find SIA-licensed security near you."
            />
            <PageHero
                image={CITY_IMAGES[0]}
                imageAlt="Glasgow skyline at night — Xtreme Protection Scotland locations"
                kicker="Scotland Security Operations // The Territory"
                title="Security Services Across"
                titleAccent="46+ Scottish Locations"
                sub="National resources with genuinely local teams — from the Highlands to the Borders, the officer at your gate knows your streets and your local risk landscape."
                testidPrefix="locations-index"
            />
            <CoverageChecker />
            <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-20">
                <Reveal>
                    <p className="font-mono-xp text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-4">Regional Commands</p>
                    <h2 className="font-display uppercase font-extrabold text-2xl sm:text-3xl tracking-wide text-white leading-none mb-8">
                        Browse by <span className="text-[#E53E3E]">region</span>
                    </h2>
                </Reveal>
                <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
                    {[
                        { slug: "central-belt", name: "Central Belt", sub: "Glasgow, Edinburgh, Stirling & the M8 corridor — 16 towns", img: 0 },
                        { slug: "north-east", name: "North East & Tayside", sub: "Aberdeen, Dundee & the energy coast — 8 towns", img: 3 },
                        { slug: "highlands-islands", name: "Highlands & Islands", sub: "Orkney, Shetland, Skye & the Western Isles — 9 towns", img: 4 },
                        { slug: "south-scotland", name: "South Scotland", sub: "Ayrshire, the Borders & Dumfries — 6 towns", img: 1 },
                    ].map((r) => (
                        <Reveal key={r.slug}>
                            <Link
                                to={`/regions/${r.slug}`}
                                data-testid={`locations-region-${r.slug}`}
                                className="group relative block overflow-hidden border border-white/10 hover:border-[#E53E3E]/70 transition-colors duration-300 h-40"
                            >
                                <img
                                    src={CITY_IMAGES[r.img]}
                                    alt={`${r.name} security services Scotland`}
                                    className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-45 group-hover:scale-105 transition-all duration-700"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
                                <div className="relative h-full p-6 flex items-center justify-between gap-4">
                                    <div>
                                        <h3 className="font-display uppercase font-extrabold text-xl sm:text-2xl tracking-wide text-white leading-none group-hover:text-[#E53E3E] transition-colors">
                                            {r.name}
                                        </h3>
                                        <p className="text-gray-400 text-sm mt-2">{r.sub}</p>
                                    </div>
                                    <ArrowRight size={16} className="text-gray-500 group-hover:text-[#E53E3E] group-hover:translate-x-1 transition-all duration-300 shrink-0" />
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                    {CITIES.map((c, i) => (
                        <Reveal key={c.slug} delay={(i % 3) * 0.06}>
                            <Link
                                to={`/locations/${c.slug}`}
                                data-testid={`locations-card-${c.slug}`}
                                className="group relative block h-56 overflow-hidden"
                            >
                                <img
                                    src={CITY_IMAGES[i % CITY_IMAGES.length]}
                                    alt={`Security services in ${c.name}, Scotland`}
                                    className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-700"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
                                <div className="relative h-full flex flex-col justify-end p-6">
                                    <span className="font-mono-xp text-[10px] uppercase tracking-[0.25em] text-[#E53E3E]">{c.region}</span>
                                    <span className="flex items-center justify-between mt-1">
                                        <h2 className="font-display uppercase font-bold text-2xl tracking-wide text-white group-hover:text-[#E53E3E] transition-colors">
                                            Security {c.name}
                                        </h2>
                                        <ArrowRight size={16} className="text-gray-500 group-hover:text-[#E53E3E] group-hover:translate-x-1 transition-all duration-300" />
                                    </span>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>
            <CtaBand />
        </div>
    );
}
